import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ExternalLink, Loader2, Plus, Search, Trash2 } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useOrganizationsQuery, useDeleteOrganizationMutation, useMyOrganizationRolesQuery } from "@/hooks/queries/useOrganizationsQuery";
import { RoleType } from "@/types/project";
import type { Organization, DeleteOrganizationCommand } from "@/types/organization";
import { toast } from "sonner";

const PAGE_SIZE = 20;

const OrganizationList = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<Organization | null>(null);

  const { data, isLoading } = useOrganizationsQuery({ name: searchTerm || undefined, page, pageSize: PAGE_SIZE, sortBy: "name", sortDirection: "asc" });
  const { data: myRoles = {} } = useMyOrganizationRolesQuery();
  const deleteOrganizationMutation = useDeleteOrganizationMutation();
  const organizations = data?.items ?? [];

  const canDeleteOrganization = (organization: Organization) => myRoles[organization.id] === RoleType.Owner;
  const isDeleteBlocked = (organization: Organization) => organization.creditBalance > 0 || organization.projectCount > 0;

  const requestDelete = (organization: Organization) => {
    if (isDeleteBlocked(organization)) {
      toast.error(t("organizationDeleteBlocked"));
      return;
    }
    setDeleteTarget(organization);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const command: DeleteOrganizationCommand = { id: deleteTarget.id, rowVersion: deleteTarget.rowVersion };
    await deleteOrganizationMutation.mutateAsync({ id: deleteTarget.id, command });
    setDeleteTarget(null);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t("organizations")}</h1>
            <p className="text-muted-foreground">{t("organizationsSubtitle")}</p>
          </div>
          <Button className="w-full gap-2 sm:w-auto" onClick={() => navigate("/organization/add")}><Plus size={18} />{t("addOrganization")}</Button>
        </div>
        <div className="flex flex-wrap items-center gap-4 p-4 bg-card rounded-lg border border-border">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input placeholder={t("searchOrganizations")} value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }} className="pl-10" />
          </div>
        </div>
        <div className="bg-card rounded-lg border border-border">
          {isLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" size={32} /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("organizationName")}</TableHead>
                  <TableHead>{t("organizationCreditBalance")}</TableHead>
                  <TableHead>{t("organizationProjects")}</TableHead>
                  <TableHead>{t("organizationOwner")}</TableHead>
                  <TableHead className="w-[140px]">{t("actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {organizations.map((organization) => (
                  <TableRow key={organization.id} className="cursor-pointer hover:bg-muted/50" onDoubleClick={() => navigate(`/organization/${organization.id}`)}>
                    <TableCell className="font-medium"><div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" />{organization.name}</div></TableCell>
                    <TableCell>{organization.creditBalance.toFixed(2)}</TableCell>
                    <TableCell>{organization.projectCount}</TableCell>
                    <TableCell>{organization.owner || "-"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => navigate(`/organization/${organization.id}`)} aria-label={t("openOrganization")}><ExternalLink size={14} /></Button>
                        {canDeleteOrganization(organization) && (
                          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={(e) => { e.stopPropagation(); requestDelete(organization); }} aria-label={t("delete")} disabled={isDeleteBlocked(organization)}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {organizations.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">{t("noOrganizationsFound")}</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </div>
        {(data?.totalPages ?? 1) > 1 && (
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1}>{t("previous")}</Button>
            <Button variant="outline" onClick={() => setPage((current) => Math.min(data?.totalPages ?? 1, current + 1))} disabled={page >= (data?.totalPages ?? 1)}>{t("next")}</Button>
          </div>
        )}
      </div>
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirmDeleteOrganization")}</AlertDialogTitle>
            <AlertDialogDescription>{t("confirmDeleteOrganizationDesc")} <strong>{deleteTarget?.name}</strong>?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteOrganizationMutation.isPending}>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteOrganizationMutation.isPending} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleteOrganizationMutation.isPending && <Loader2 className="animate-spin mr-2" size={16} />}{t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default OrganizationList;
